import React, { useState, useEffect } from "react";
import DataGridTablePage from "../../../Components/DataGridTablePage/DataGridTablePage";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Snackbar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { httpclient } from "../../../utils/httpClient";

const OrderImportAlert = () => {
  const loginData = localStorage.getItem("login");
  const loginValue = JSON.parse(loginData);

  const fieldHeaderPairs = [
    {
      field: "marketplace",
      headerName: "MarketPlace",
      width: 200,
      renderCell: (params) => params.row?.marketplace || "-",
    },
    { field: "startTime", headerName: "Start Time", width: 120 },
    { field: "endTime", headerName: "End Time", width: 120 },
    { field: "timeInterval", headerName: "Interval (hr)", width: 130 },
    {
      field: "id",
      headerName: "Actions",
      width: 180,
      renderCell: (params) => (
        <Grid container spacing={1}>
          <Grid item>
            <Button
              variant="outlined"
              size="small"
              onClick={() => editHolidayDataFill(params.row.id)}
            >
              Edit
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={() => openDeleteConfirm(params.row.id)}
            >
              Delete
            </Button>
          </Grid>
        </Grid>
      ),
    },
  ];

  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [formValue, setFormValue] = useState({
    startTime: "",
    endTime: "",
    timeInterval: "",
  });
  
  // Stores the name of the selected marketplace
  const [selectedMarketplace, setSelectedMarketplace] = useState("");
  
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageState, setMessageState] = useState("");
  const [isDataChanged, setIsDataChanged] = useState(1);
  const [editId, setEditId] = useState(null);

  // State for Delete Confirmation Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);

  // Fetch branches on mount
  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await httpclient.get("new-system/netsuite/company-account/all");
      setBranches(response.data.data || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  // Function to open the delete confirmation dialog
  const openDeleteConfirm = (id) => {
    setIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Function to handle the actual deletion after confirmation
  const confirmDelete = () => {
    setDeleteDialogOpen(false);
    
    httpclient
      .delete(`setting/delete/alert-interval/${idToDelete}`)
      .then(({ data }) => {
        setOpen(true);
        setMessageState(data.success ? "success" : "error");
        setMessage(data.message || data.error);
        if (data.success) setIsDataChanged((prev) => (prev === 1 ? 0 : 1));
      });
  };

  const handleChangeFormData = (e) => {
    const { name, value } = e.target;
    setFormValue((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const editHolidayDataFill = (id) => {
    setEditLoading(true);
    httpclient
      .get(`setting/edit/alert-interval/${id}`)
      .then(({ data }) => {
        if (data.success) {
          const item = data.data;

          setFormValue({
            startTime: item.startTime || "",
            endTime: item.endTime || "",
            timeInterval: item.timeInterval ? String(item.timeInterval) : "",
          });

          // Set the selected marketplace name
          setSelectedMarketplace(item.marketplace || "");

          setEditId(id);
        }
        setEditLoading(false);
      })
      .catch(() => setEditLoading(false));
  };

  const handleAddEditHoliday = () => {
    // Validation using MUI Snackbar instead of alert
    if (
      !formValue.startTime ||
      !formValue.endTime ||
      !formValue.timeInterval ||
      parseInt(formValue.timeInterval) <= 0 ||
      !selectedMarketplace
    ) {
      setMessageState("error");
      setMessage("Please fill all required fields correctly.");
      setOpen(true);
      return;
    }

    setLoading(true);
    const endpoint = editId
      ? `setting/update/alert-interval/${editId}`
      : `setting/create/alert-interval`;

    // Find the full branch object to get the ID
    const selectedBranch = branches.find((b) => b.name === selectedMarketplace);

    // Constructing the payload
    const payload = {
      startTime: formValue.startTime,
      endTime: formValue.endTime,
      timeInterval: parseInt(formValue.timeInterval, 10),
      marketplace: selectedMarketplace, // The Name
      companyID: selectedBranch ? selectedBranch.id : null,
      userID: loginValue.userID,
    };

    if (!payload.companyID) {
        setOpen(true);
        setMessageState("error");
        setMessage("Error identifying selected marketplace ID.");
        setLoading(false);
        return;
    }

    httpclient
      .post(endpoint, payload)
      .then(({ data }) => {
        setOpen(true);
        setMessageState(data.success ? "success" : "error");
        setMessage(data.message || data.error);

        if (data.success) {
          setIsDataChanged((prev) => (prev === 1 ? 0 : 1));
          // Reset form
          setFormValue({
            startTime: "",
            endTime: "",
            timeInterval: "",
          });
          setSelectedMarketplace("");
          setEditId(null);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setOpen(true);
        setMessageState("error");
        setMessage("An error occurred. Please try again.");
      });
  };

  return (
    <>
      <Grid container spacing={2}>
        {/* Data Grid */}
        <Grid item md={6} xs={12}>
          <DataGridTablePage
            dataChanged={isDataChanged}
            headerTitle="Order Import Alert Schedules"
            exportFeature={false}
            customIDKey="id"
            columns={fieldHeaderPairs}
            pageUrl="setting/get/alert-interval"
            columnsVisibility={{ userID: false }}
          />
        </Grid>

        {/* Form */}
        <Grid item md={6} xs={12}>
          <h1>{editId ? "Edit Order Import Alert Schedule" : "Schedule New Order Import Alert"}</h1>
          <Box mt={5} p={2} style={{ background: "#fff" }}>
            
            {/* Time & Interval */}
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              <TextField
                label="Start Time"
                type="time"
                variant="outlined"
                name="startTime"
                value={formValue.startTime}
                onChange={handleChangeFormData}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                sx={{ minWidth: 160 }}
              />
              <TextField
                label="End Time"
                type="time"
                variant="outlined"
                name="endTime"
                value={formValue.endTime}
                onChange={handleChangeFormData}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                sx={{ minWidth: 160 }}
              />
              <TextField
                label="Interval (hr)"
                type="number"
                variant="outlined"
                name="timeInterval"
                value={formValue.timeInterval}
                onChange={handleChangeFormData}
                inputProps={{ min: 1, step: 1 }}
                sx={{ minWidth: 160 }}
              />
            </Box>

            {/* Marketplace Selection - Dropdown */}
            <Box mt={3} mb={2}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="marketplace-select-label">Select Marketplace</InputLabel>
                <Select
                  labelId="marketplace-select-label"
                  value={selectedMarketplace}
                  onChange={(e) => setSelectedMarketplace(e.target.value)}
                  label="Select Marketplace"
                >
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.name}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Action Buttons */}
            <Box pt={2} textAlign="right">
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleAddEditHoliday}
                    disabled={
                      !formValue.startTime ||
                      !formValue.endTime ||
                      !formValue.timeInterval ||
                      parseInt(formValue.timeInterval) <= 0 ||
                      !selectedMarketplace
                    }
                    sx={{ mt: 1 }}
                  >
                    {editId ? "Update" : "Add"}
                  </Button>
                  {editId && (
                    <Button
                      color="secondary"
                      variant="outlined"
                      onClick={() => {
                        setEditId(null);
                        setFormValue({
                          startTime: "",
                          endTime: "",
                          timeInterval: "",
                        });
                        setSelectedMarketplace("");
                      }}
                      sx={{ mt: 1, ml: 1 }}
                    >
                      Cancel
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Snackbar for Success/Error Messages */}
      <Snackbar
        open={open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={handleCloseSnack}
      >
        <Alert onClose={handleCloseSnack} severity={messageState} sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Delete"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this order import alert?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" autoFocus variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderImportAlert;